import { Video, VideoRemove } from 'iconsax-react';
import mergeRefs from 'merge-refs';
import { forwardRef, type ReactEventHandler, useRef, type VideoHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean, useEventListener } from 'usehooks-ts';
import PlayIcon from '@/assets/images/icons/play.svg?react';
import DurationDisplay from '@/components/common/duration-display';
import { calculateImageDimension } from '@/components/common/media-content/image-dimension';
import { SafeImage } from '@/components/common/safe-image';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import { useCalculateVideoPosterLayout } from '@/hooks/use-calculate-video-poster-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import { getPreviewableUrl, updateUrlPlaybackTime } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';

type VideoProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'poster'> & {
  poster?: string | boolean | undefined;
  isInView?: boolean;
  respectRatio?: boolean;
  wrapperClassName?: string;
  playBackTime?: number;
};

/**
 * Hack for iOS devices to show video thumbnail
 */
export const VideoViewer = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      playBackTime,
      preload = 'metadata',
      className,
      playsInline,
      src,
      controls,
      onLoadedMetadata,
      onError,
      poster = true,
      autoPlay = true,
      isInView = true,
      respectRatio = true,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { isIOS, isDesktop } = useUserPlatform();

    const videoRef = useRef<HTMLVideoElement>(null);

    const { value: hasError, setTrue: setHasError } = useBoolean(false);

    const { setDimension, setDuration, hasPoster, posterUrl, dimension, setHasNoPoster, safeSrc, duration } =
      useCalculateVideoPosterLayout({
        src,
        poster,
      });
    const { setMediaPlaying, isMediaPlaying } = useConversationStore();

    useEventListener(
      'playing',
      () => {
        if (isMediaPlaying !== safeSrc) {
          setMediaPlaying(safeSrc);
        }
      },
      videoRef,
    );

    const handleLoadedMetadata: ReactEventHandler<HTMLVideoElement> = (event) => {
      const videoElement = event.target as HTMLVideoElement;
      const { duration, videoWidth, videoHeight } = videoElement;
      setDuration(duration);
      setDimension(calculateImageDimension(videoWidth, videoHeight));
      onLoadedMetadata?.(event);
    };

    const handlePlay = () => {
      videoRef.current?.play().catch(console.warn);
    };

    if (src?.startsWith('data:')) {
      return (
        <div className={cn('p-2 flex flex-center bg-neutral-0', className)}>
          <Video
            variant='Outline'
            className='size-8 text-neutral-300'
          />
        </div>
      );
    }

    if (!isInView) return null;

    if (hasError) {
      return (
        <div className={cn('p-2 flex flex-center bg-neutral-0', className)}>
          <VideoRemove
            variant='Outline'
            className='size-8 text-neutral-300'
          />
        </div>
      );
    }

    if (!autoPlay && hasPoster && posterUrl) {
      return (
        <div className={cn('relative', { 'size-full': !respectRatio }, wrapperClassName)}>
          <SafeImage
            src={posterUrl}
            style={
              respectRatio
                ? {
                    width: dimension[0] ?? '100%',
                    height: dimension[1] ?? (isDesktop ? '480px' : '194px'),
                    aspectRatio: dimension[2] ?? '16/9',
                  }
                : undefined
            }
            className={cn('bg-cover', { 'size-full': !respectRatio }, className)}
            onImageError={() => setHasNoPoster()}
          />

          <PlayIndicator
            duration={duration}
            onClick={handlePlay}
          />
        </div>
      );
    }

    return (
      <div className={cn('relative size-full flex flex-col flex-center', wrapperClassName)}>
        <video
          ref={mergeRefs(videoRef, ref)}
          className={className}
          playsInline={isIOS || playsInline}
          src={
            isIOS
              ? getPreviewableUrl(updateUrlPlaybackTime(safeSrc, playBackTime))
              : updateUrlPlaybackTime(safeSrc, playBackTime)
          }
          controls={controls}
          poster={posterUrl}
          autoPlay={!isIOS && autoPlay}
          onError={(event) => {
            onError?.(event);
            setHasError();
          }}
          onLoadedMetadata={handleLoadedMetadata}
          {...props}>
          {t('errors.video-not-supported')}
        </video>
      </div>
    );
  },
);
export default VideoViewer;

interface PlayIndicatorProps {
  duration: number;
  onClick: () => void;
}

function PlayIndicator({ duration, onClick }: PlayIndicatorProps) {
  return (
    <div className='z-10 absolute start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-neutral-0 flex flex-col items-center'>
      <button
        type='button'
        onClick={() => onClick()}
        className='btn btn-ghost btn-sm btn-circle'>
        <PlayIcon className='size-8 drop-shadow' />
      </button>

      <DurationDisplay
        withUnits={false}
        duration={duration}
        className='font-light text-shadow'
      />
    </div>
  );
}
